import { FloatingIsland } from '../FloatingIsland';
import { DisplayMode } from '@rocco/types/enum/display-mode';

import {
    DetailsPageIslandImage,
    type DetailsPageIslandImageProps,
} from '../detailspage-island/DetailsPageIslandImage';

interface DetailsPageIslandProps {
    image?: DetailsPageIslandImageProps;
    title: string;
    description: string | React.ReactNode;
    primaryAction: React.ReactNode;
    secondaryAction: React.ReactNode;
    displayMode: DisplayMode;
    scrollThreshold?: number;
    isClickable?: boolean;
    onClick?: () => void;
    className?: string;
}

export const DetailsPageIsland = ({
    image,
    title,
    description,
    primaryAction,
    secondaryAction,
    displayMode,
    scrollThreshold = 100,
    isClickable = true,
    onClick,
    className,
}: DetailsPageIslandProps) => {
    const showImage = image && displayMode === DisplayMode.Desktop;
    const variant = showImage ? 'details' : 'detailsNoImage';

    return (
        // Keep the island mounted but hidden so animations can work properly
        // The outer wrapper will handle pointer events via pointer-events-none
        <FloatingIsland
            scrollThreshold={scrollThreshold}
            variant={variant}
            unmountOnHidden={false}
            className={className}
        >
            <div className="flex items-center gap-2.5 md:min-h-18 md:gap-6">
                <div className="hover:cursor-pointer" onClick={onClick}>
                    {showImage ? <DetailsPageIslandImage {...image} /> : null}
                </div>

                <div className="flex w-fit min-w-0 flex-1 flex-col items-start gap-0">
                    <a
                        className={`w-full overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-white md:text-base md:leading-[1.25] ${
                            isClickable
                                ? 'hover:text-primary-600 cursor-pointer'
                                : 'cursor-default'
                        }`}
                        onClick={isClickable ? onClick : undefined}
                    >
                        {title}
                    </a>
                    {typeof description === 'string' ? (
                        <p className="cursor-default overflow-hidden text-ellipsis text-3 font-medium tracking-[0.0175rem] text-white md:text-sm">
                            {description}
                        </p>
                    ) : (
                        description
                    )}
                </div>
                <div className="flex shrink-0 gap-2.5">
                    {secondaryAction}
                    {primaryAction}
                </div>
            </div>
        </FloatingIsland>
    );
};
